import React, { useState, useEffect } from 'react';
import { Users, UserCircle, AlertCircle, Plus, X, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { UserRole, isLeadRecruiter, getRoleBadgeClass, getRoleDisplayName } from '../utils/roleUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Team Performance Statistics Component
const TeamPerformanceSection = ({ teamId }) => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMemberDetails, setShowMemberDetails] = useState(null);

  useEffect(() => {
    if (teamId) {
      fetchTeamStatistics();
    }
  }, [teamId]);

  const fetchTeamStatistics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/team-management/teams/${teamId}/statistics`);
      if (!response.ok) {
        throw new Error('Failed to fetch team statistics');
      }
      const data = await response.json();
      setStatistics(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching team statistics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    if (hours > 0) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-500 mt-2">Loading team statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="bg-red-50 rounded-lg p-4 text-center text-red-600">
          <AlertCircle className="h-5 w-5 mx-auto mb-2" />
          <p className="text-sm">Failed to load team statistics</p>
        </div>
      </div>
    );
  }

  if (!statistics || !statistics.team_members || statistics.team_members.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 pt-6 border-t border-gray-200">
      <div className="mb-4">
        <h5 className="font-medium text-gray-900 flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          Team Performance & Contributions
        </h5>
        <p className="text-xs text-gray-500 mt-1">
          Track individual member contributions and workload distribution
        </p>
      </div>

      {/* Team Totals Summary */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
          <div className="text-xs text-blue-700 font-medium">Total Companies</div>
          <div className="text-2xl font-bold text-blue-900 mt-1">
            {statistics.team_totals?.total_companies || 0}
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
          <div className="text-xs text-green-700 font-medium">Total Jobs</div>
          <div className="text-2xl font-bold text-green-900 mt-1">
            {statistics.team_totals?.total_jobs || 0}
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
          <div className="text-xs text-purple-700 font-medium">Total Candidates</div>
          <div className="text-2xl font-bold text-purple-900 mt-1">
            {statistics.team_totals?.total_candidates || 0}
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
          <div className="text-xs text-orange-700 font-medium">Total Submissions</div>
          <div className="text-2xl font-bold text-orange-900 mt-1">
            {statistics.team_totals?.total_submissions || 0}
          </div>
        </div>
      </div>

      {/* Member Performance Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Companies
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Jobs
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidates
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submissions
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {statistics.team_members.map(member => (
                <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <UserCircle className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{member.name}</div>
                        <div className="text-xs text-gray-500">{member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-800 rounded-lg text-sm font-semibold">
                      {member.statistics.companies_created}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-green-100 text-green-800 rounded-lg text-sm font-semibold">
                      {member.statistics.jobs_created}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-purple-100 text-purple-800 rounded-lg text-sm font-semibold">
                      {member.statistics.candidates_created}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-orange-100 text-orange-800 rounded-lg text-sm font-semibold">
                      {member.statistics.submissions_created}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {formatRelativeTime(member.statistics.last_activity)}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => setShowMemberDetails(showMemberDetails === member.id ? null : member.id)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      {showMemberDetails === member.id ? 'Hide' : 'View'} Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Member Details Modal/Expansion */}
      {showMemberDetails && statistics.team_members.map(member => {
        if (member.id !== showMemberDetails) return null;
        
        return (
          <div key={member.id} className="mt-4 bg-gray-50 rounded-lg border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h6 className="text-lg font-semibold text-gray-900">{member.name}'s Recent Activity</h6>
              <button
                onClick={() => setShowMemberDetails(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Recent Companies */}
              <div>
                <h6 className="text-sm font-semibold text-gray-700 mb-3">Recent Companies</h6>
                <div className="space-y-2">
                  {member.recent_entities.companies?.length > 0 ? (
                    member.recent_entities.companies.map(company => (
                      <div key={company.id} className="bg-white rounded p-3 border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-sm text-gray-900">{company.client_name}</p>
                            <p className="text-xs text-gray-500">{company.display_id}</p>
                          </div>
                          <span className="text-xs text-gray-400">
                            {formatRelativeTime(company.created_at)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No companies yet</p>
                  )}
                </div>
              </div>

              {/* Recent Jobs */}
              <div>
                <h6 className="text-sm font-semibold text-gray-700 mb-3">Recent Jobs</h6>
                <div className="space-y-2">
                  {member.recent_entities.jobs?.length > 0 ? (
                    member.recent_entities.jobs.map(job => (
                      <div key={job.id} className="bg-white rounded p-3 border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-sm text-gray-900">{job.title}</p>
                            <p className="text-xs text-gray-500">{job.client}</p>
                          </div>
                          <span className="text-xs text-gray-400">
                            {formatRelativeTime(job.created_at)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No jobs yet</p>
                  )}
                </div>
              </div>

              {/* Recent Candidates */}
              <div>
                <h6 className="text-sm font-semibold text-gray-700 mb-3">Recent Candidates</h6>
                <div className="space-y-2">
                  {member.recent_entities.candidates?.length > 0 ? (
                    member.recent_entities.candidates.map(candidate => (
                      <div key={candidate.id} className="bg-white rounded p-3 border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-sm text-gray-900">{candidate.name}</p>
                            <p className="text-xs text-gray-500">{candidate.current_company}</p>
                          </div>
                          <span className="text-xs text-gray-400">
                            {formatRelativeTime(candidate.created_at)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No candidates yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TeamManagementTab = ({ leadRecruiters, accountManagers, recruiterSupervisors, teams, allRecruiters, allUsers, onAddMember, onRemoveMember, onRefresh }) => {
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedRecruitersToAdd, setSelectedRecruitersToAdd] = useState([]);
  const [selectedMembersToRemove, setSelectedMembersToRemove] = useState([]);
  const [teamTypeFilter, setTeamTypeFilter] = useState('lead'); // 'lead', 'recruiter_supervisor', or 'account_manager'
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [newTeam, setNewTeam] = useState({
    team_name: '',
    team_lead_id: '',
    assigned_clients: []
  });

  // Get team for selected lead
  const selectedTeam = teams.find(t => t.team_lead_id === selectedLeadId);
  const selectedLeadData = allUsers.find(u => u.id === selectedLeadId);
  
  // Get team leads based on filter
  const getTeamLeads = () => {
    if (teamTypeFilter === 'lead') {
      return leadRecruiters;
    } else if (teamTypeFilter === 'recruiter_supervisor') {
      return recruiterSupervisors;
    } else {
      // account_manager
      return allUsers.filter(u => u.role === 'account_manager' || u.roles?.includes('account_manager'));
    }
  };
  
  const teamLeads = getTeamLeads();

  // Get available members based on team lead's role
  const getAvailableMembers = () => {
    if (!selectedLeadData) return [];
    
    // Check which users are already MEMBERS of teams
    const usersAsMembersInTeams = new Set();
    teams.forEach(team => {
      (team.team_members || []).forEach(memberId => usersAsMembersInTeams.add(memberId));
    });
    
    // Check which users are LEADS of teams
    const usersAsTeamLeads = new Set();
    teams.forEach(team => {
      if (team.team_lead_id) {
        usersAsTeamLeads.add(team.team_lead_id);
      }
    });
    
    // Recruiter Supervisor: Team of Lead Recruiters + unassigned recruiters
    if (selectedLeadData.role === 'recruiter_supervisor') {
      return allUsers.filter(u => {
        // Don't add self
        if (u.id === selectedLeadId) return false;
        
        // For Lead Recruiters: Can add even if they lead their own team (that's the point!)
        // But NOT if they're already a MEMBER of this or another supervisor team
        if (isLeadRecruiter(u.role)) {
          return !usersAsMembersInTeams.has(u.id);
        }
        
        // For regular Recruiters: Only if not in ANY team (as member or lead)
        if (u.role === UserRole.RECRUITER) {
          return !usersAsMembersInTeams.has(u.id) && !usersAsTeamLeads.has(u.id);
        }
        
        return false;
      });
    }
    
    // Lead Recruiter can have recruiters only (not in any team)
    if (isLeadRecruiter(selectedLeadData.role)) {
      return allUsers.filter(u => 
        u.role === UserRole.RECRUITER && 
        u.id !== selectedLeadId && 
        !usersAsMembersInTeams.has(u.id) &&
        !usersAsTeamLeads.has(u.id)
      );
    }
    
    // Account Manager can have recruiters and account executives
    if (selectedLeadData.role === 'account_manager' || selectedLeadData.roles?.includes('account_manager')) {
      return allUsers.filter(u => 
        (u.role === 'recruiter' || u.role === 'account_exec' || u.role === 'lead_recruiter') && 
        u.id !== selectedLeadId && 
        !usersAsMembersInTeams.has(u.id) &&
        !usersAsTeamLeads.has(u.id)
      );
    }
    
    return [];
  };
  
  const availableMembers = getAvailableMembers();
  
  // Reset selections when modal closes or lead changes
  useEffect(() => {
    setSelectedRecruitersToAdd([]);
    setSelectedMembersToRemove([]);
  }, [selectedLeadId, showAddMemberModal]);
  
  const toggleMemberSelection = (userId) => {
    setSelectedRecruitersToAdd(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };
  
  const toggleMemberRemoval = (memberId) => {
    setSelectedMembersToRemove(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };
  
  const handleBulkAddMembers = async () => {
    if (selectedRecruitersToAdd.length === 0) {
      toast.error('Please select at least one member to add');
      return;
    }
    
    try {
      let successCount = 0;
      
      for (const userId of selectedRecruitersToAdd) {
        try {
          await onAddMember(selectedTeam.id, userId);
          successCount++;
        } catch (error) {
          console.error(`Failed to add member ${userId}:`, error);
        }
      }
      
      if (successCount > 0) {
        toast.success(`Added ${successCount} member(s) to team`);
      }
      setSelectedRecruitersToAdd([]);
      setShowAddMemberModal(false);
    } catch (error) {
      toast.error('Failed to add members');
    }
  };
  
  const handleBulkRemoveMembers = async () => {
    if (selectedMembersToRemove.length === 0) {
      toast.error('Please select at least one member to remove');
      return;
    }
    
    if (!window.confirm(`Remove ${selectedMembersToRemove.length} member(s) from team?`)) {
      return;
    }
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      // Remove all selected members
      for (const memberId of selectedMembersToRemove) {
        try {
          await onRemoveMember(selectedTeam.id, memberId);
          successCount++;
        } catch (error) {
          console.error(`Failed to remove member ${memberId}:`, error);
          failCount++;
          toast.error(`Failed to remove member: ${error.message}`);
        }
      }
      
      if (successCount > 0) {
        toast.success(`Removed ${successCount} member(s) from team`);
        onRefresh(); // Refresh the teams list
      }
      
      if (failCount > 0) {
        toast.error(`Failed to remove ${failCount} member(s)`);
      }
      
      setSelectedMembersToRemove([]);
    } catch (error) {
      console.error('Bulk remove error:', error);
      toast.error(`Failed to remove members: ${error.message}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Toggle Filter */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Team Management</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage teams and assign members. Each person can only belong to one team.
            </p>
          </div>
          <button
            onClick={() => setShowCreateTeamModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 shadow-md"
            data-testid="create-new-team-button"
          >
            <Plus className="h-5 w-5" />
            Create New Team
          </button>
        </div>
        
        {/* Team Type Toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => {
              setTeamTypeFilter('lead');
              setSelectedLeadId(null);
            }}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              teamTypeFilter === 'lead'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Recruiting Leads
          </button>
          <button
            onClick={() => {
              setTeamTypeFilter('recruiter_supervisor');
              setSelectedLeadId(null);
            }}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              teamTypeFilter === 'recruiter_supervisor'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Recruiting Supervisor
          </button>
          <button
            onClick={() => {
              setTeamTypeFilter('account_manager');
              setSelectedLeadId(null);
            }}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              teamTypeFilter === 'account_manager'
                ? 'bg-orange-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Account Managers
          </button>
        </div>
      </div>

      {/* Team Leads List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">
            {teamTypeFilter === 'lead' ? 'Recruiting Lead Teams' : 
             teamTypeFilter === 'recruiter_supervisor' ? 'Recruiting Supervisor Teams' : 
             'Account Manager Teams'}
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            {teamTypeFilter === 'lead' 
              ? 'Lead Recruiters who manage teams of recruiters'
              : teamTypeFilter === 'recruiter_supervisor'
              ? 'Recruiter Supervisors who oversee multiple recruiting teams'
              : 'Account Managers who manage client relationships and teams'}
          </p>
        </div>

        {teamLeads.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No {teamTypeFilter} team leads found</p>
            <p className="text-sm mt-2">Create users with appropriate roles to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {teamLeads.map((lead) => {
              const team = teams.find(t => t.team_lead_id === lead.id);
              const isExpanded = selectedLeadId === lead.id;

              return (
                <div key={lead.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <Users className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-lg font-semibold text-gray-900">{lead.full_name}</h4>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadgeClass(lead.role)}`}>
                            {getRoleDisplayName(lead.role)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{lead.email}</p>
                        {lead.industries && lead.industries.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            Industries: {lead.industries.join(', ')}
                          </p>
                        )}
                        {team && (
                          <p className="text-xs text-purple-600 mt-1">
                            Team: {team.team_name} • {team.team_members?.length || 0} members
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedLeadId(isExpanded ? null : lead.id)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        {isExpanded ? 'Hide Team' : 'Manage Team'}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Team Management */}
                  {isExpanded && (
                    <div className="mt-6 pl-16">
                      {!team ? (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                          <AlertCircle className="h-8 w-8 text-yellow-600 mx-auto mb-3" />
                          <p className="text-sm text-yellow-800 mb-4">No team created for this lead yet</p>
                          <button
                            onClick={async () => {
                              try {
                                const teamType = lead.role === 'account_manager' ? 'account_management' : 'recruiting';
                                const teamDescription = teamType === 'account_management' 
                                  ? `Account management team led by ${lead.full_name}`
                                  : lead.role === 'recruiter_supervisor'
                                    ? `Recruiting operations team led by ${lead.full_name}`
                                    : `Recruiting team led by ${lead.full_name}`;
                                
                                const response = await fetch(`${BACKEND_URL}/api/teams`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    team_name: `${lead.full_name}'s Team`,
                                    team_type: teamType,
                                    team_lead_id: lead.id,
                                    team_lead_name: lead.full_name,
                                    team_lead_role: lead.role,
                                    description: teamDescription
                                  })
                                });
                                
                                if (response.ok) {
                                  toast.success('Team created successfully!');
                                  onRefresh();
                                } else {
                                  const error = await response.json();
                                  toast.error(error.detail || 'Failed to create team');
                                }
                              } catch (error) {
                                console.error('Error creating team:', error);
                                toast.error('Failed to create team');
                              }
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 mx-auto"
                          >
                            <Plus className="h-4 w-4" />
                            Create Team
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h5 className="font-medium text-gray-900">Team Members ({team.team_members?.length || 0})</h5>
                            <button
                              onClick={() => setShowAddMemberModal(true)}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-2"
                            >
                              <Plus className="h-4 w-4" />
                              Add Member
                            </button>
                          </div>

                          {team.team_members?.length === 0 ? (
                            <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500 text-sm">
                              No team members yet. Click "Add Member" to get started.
                            </div>
                          ) : (
                            <>
                              {selectedMembersToRemove.length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
                                  <span className="text-sm text-red-800">
                                    {selectedMembersToRemove.length} member(s) selected
                                  </span>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => setSelectedMembersToRemove([])}
                                      className="px-3 py-1 bg-white text-gray-700 text-sm rounded-lg hover:bg-gray-100"
                                    >
                                      Clear
                                    </button>
                                    <button
                                      onClick={handleBulkRemoveMembers}
                                      className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                                    >
                                      Remove Selected
                                    </button>
                                  </div>
                                </div>
                              )}
                              <div className="space-y-2">
                                {team.team_members.map((memberId) => {
                                  const member = allUsers.find(u => u.id === memberId);
                                  if (!member) return null;
                                  
                                  const isSelected = selectedMembersToRemove.includes(memberId);

                                  return (
                                    <div key={memberId} className={`flex items-center gap-3 bg-gray-50 rounded-lg p-3 ${isSelected ? 'ring-2 ring-red-400 bg-red-50' : ''}`}>
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleMemberRemoval(memberId)}
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                      />
                                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                        <UserCircle className="h-5 w-5 text-blue-600" />
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <p className="font-medium text-gray-900">{member.full_name}</p>
                                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeClass(member.role)}`}>
                                            {getRoleDisplayName(member.role).toLowerCase()}
                                          </span>
                                        </div>
                                        <p className="text-xs text-gray-600">{member.email}</p>
                                        {member.industries && member.industries.length > 0 && (
                                          <p className="text-xs text-purple-600 mt-1">
                                            Industries: {member.industries.join(', ')}
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex gap-2">
                                        <button
                                          onClick={async () => {
                                            if (window.confirm(`Remove ${member.full_name} from team?`)) {
                                              try {
                                                await onRemoveMember(team.id, memberId);
                                                toast.success(`${member.full_name} removed from team`);
                                                onRefresh(); // Refresh the teams list
                                              } catch (error) {
                                                console.error('Error removing member:', error);
                                                toast.error(`Failed to remove ${member.full_name}: ${error.message}`);
                                              }
                                            }
                                          }}
                                          className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200 transition-colors"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          )}

                          {/* Assigned Clients Section - Shows Team Lead's Clients */}
                          <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="mb-4">
                              <h5 className="font-medium text-gray-900">Team Lead's Assigned Clients</h5>
                              <p className="text-xs text-gray-500 mt-1">
                                Managed via team lead's user profile. Team members inherit these client assignments.
                              </p>
                            </div>

                            {(() => {
                              const teamLead = allUsers.find(u => u.id === team.team_lead_id);
                              const assignedClients = teamLead?.assigned_clients || [];
                              
                              return assignedClients.length === 0 ? (
                                <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500 text-sm">
                                  <p>No clients assigned to team lead yet.</p>
                                  <p className="text-xs mt-1">Edit the team lead's user profile to assign clients.</p>
                                </div>
                              ) : (
                                <div className="bg-blue-50 rounded-lg p-4">
                                  <div className="flex flex-wrap gap-2">
                                    {assignedClients.map((client, idx) => (
                                      <span
                                        key={idx}
                                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                                      >
                                        {client}
                                      </span>
                                    ))}
                                  </div>
                                  <p className="text-xs text-gray-600 mt-3">
                                    {assignedClients.length} {assignedClients.length === 1 ? 'client' : 'clients'} • 
                                    Team Lead: {teamLead?.full_name || teamLead?.name || 'Unknown'}
                                  </p>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Team Performance Statistics Section */}
                          <TeamPerformanceSection teamId={team.id} />

                          {/* Add Member Modal */}
                          {showAddMemberModal && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                              <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                                <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex justify-between items-center">
                                  <h3 className="text-xl font-bold text-white">Add Team Member</h3>
                                  <button
                                    onClick={() => setShowAddMemberModal(false)}
                                    className="text-white hover:text-gray-200"
                                  >
                                    <X className="h-6 w-6" />
                                  </button>
                                </div>

                                <div className="p-6">
                                  {availableMembers.length === 0 ? (
                                    <div className="text-center text-gray-500 py-8">
                                      <p>No available members to add</p>
                                      <p className="text-sm mt-2">All eligible users are already assigned to teams</p>
                                    </div>
                                  ) : (
                                    <div className="space-y-3">
                                      <div className="flex items-center justify-between mb-3">
                                        <label className="text-sm font-medium text-gray-700">
                                          Select Members ({selectedRecruitersToAdd.length} selected)
                                        </label>
                                        {selectedRecruitersToAdd.length > 0 && (
                                          <button
                                            onClick={() => setSelectedRecruitersToAdd([])}
                                            className="text-xs text-gray-600 hover:text-gray-800"
                                          >
                                            Clear All
                                          </button>
                                        )}
                                      </div>
                                      <div className="max-h-64 overflow-y-auto space-y-2">
                                        {availableMembers.map((user) => {
                                          const isSelected = selectedRecruitersToAdd.includes(user.id);
                                          return (
                                            <div
                                              key={user.id}
                                              onClick={() => toggleMemberSelection(user.id)}
                                              className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                                isSelected 
                                                  ? 'border-green-400 bg-green-50' 
                                                  : 'border-gray-200 hover:border-green-200 hover:bg-green-50'
                                              }`}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleMemberSelection(user.id)}
                                                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                                <UserCircle className="h-6 w-6 text-green-600" />
                                              </div>
                                              <div className="flex-1 text-left">
                                                <div className="flex items-center gap-2">
                                                  <p className="font-medium text-gray-900">{user.full_name}</p>
                                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeClass(user.role)}`}>
                                                    {getRoleDisplayName(user.role).toLowerCase()}
                                                  </span>
                                                </div>
                                                <p className="text-sm text-gray-600">{user.email}</p>
                                                {user.industries && user.industries.length > 0 && (
                                                  <p className="text-xs text-green-600 mt-1">
                                                    {user.industries.join(', ')}
                                                  </p>
                                                )}
                                              </div>
                                              {isSelected && (
                                                <Check className="h-5 w-5 text-green-600" />
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className="bg-gray-50 px-6 py-4 flex justify-between gap-3">
                                  <button
                                    onClick={() => {
                                      setShowAddMemberModal(false);
                                      setSelectedRecruitersToAdd([]);
                                    }}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleBulkAddMembers}
                                    disabled={selectedRecruitersToAdd.length === 0}
                                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                  >
                                    <Plus className="h-4 w-4" />
                                    Add {selectedRecruitersToAdd.length > 0 ? `${selectedRecruitersToAdd.length} Member(s)` : 'Members'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Assigned Clients Modal */}
      {/* Create New Team Modal */}
      {showCreateTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex justify-between items-center sticky top-0">
              <h3 className="text-xl font-bold text-white">Create New Team</h3>
              <button
                onClick={() => {
                  setShowCreateTeamModal(false);
                  setNewTeam({ team_name: '', team_lead_id: '', assigned_clients: [] });
                }}
                className="text-white hover:text-gray-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Team Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newTeam.team_name}
                  onChange={(e) => setNewTeam({ ...newTeam, team_name: e.target.value })}
                  placeholder="e.g., Enterprise Sales Team, Tech Recruiting Team"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  data-testid="team-name-input"
                />
              </div>

              {/* Team Lead Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Lead <span className="text-red-500">*</span>
                </label>
                <select
                  value={newTeam.team_lead_id}
                  onChange={(e) => setNewTeam({ ...newTeam, team_lead_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  data-testid="team-lead-select"
                >
                  <option value="">Select a team lead...</option>
                  
                  <optgroup label="Lead Recruiters">
                    {leadRecruiters.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.full_name} ({user.email})
                      </option>
                    ))}
                  </optgroup>
                  
                  <optgroup label="Recruiter Supervisors">
                    {recruiterSupervisors.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.full_name} ({user.email})
                      </option>
                    ))}
                  </optgroup>
                  
                  {accountManagers && accountManagers.length > 0 && (
                    <optgroup label="Account Managers">
                      {accountManagers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.full_name} ({user.email})
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Only users with lead_recruiter, recruiter_supervisor, or account_manager roles can be team leads
                </p>
              </div>

              {/* Assigned Clients (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Clients (Optional)
                </label>
                <input
                  type="text"
                  value={newTeam.assigned_clients.join(', ')}
                  onChange={(e) => {
                    const clients = e.target.value.split(',').map(c => c.trim()).filter(c => c);
                    setNewTeam({ ...newTeam, assigned_clients: clients });
                  }}
                  placeholder="e.g., SAP America, IBM, Microsoft (comma-separated)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  data-testid="assigned-clients-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter client company names separated by commas. You can add more later.
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">After creating the team:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>The selected user will become the team lead</li>
                      <li>You can add team members from the team list</li>
                      <li>Team members can be recruiters or other eligible users</li>
                      <li>Assigned clients will be used for job filtering</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-between gap-3 sticky bottom-0">
              <button
                onClick={() => {
                  setShowCreateTeamModal(false);
                  setNewTeam({ team_name: '', team_lead_id: '', assigned_clients: [] });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!newTeam.team_name.trim()) {
                    toast.error('Please enter a team name');
                    return;
                  }
                  if (!newTeam.team_lead_id) {
                    toast.error('Please select a team lead');
                    return;
                  }

                  try {
                    const leadUser = allUsers.find(u => u.id === newTeam.team_lead_id);
                    const teamType = leadUser?.role === 'account_manager' ? 'account_management' : 'recruiting';
                    
                    const response = await fetch(`${BACKEND_URL}/api/teams`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        team_name: newTeam.team_name.trim(),
                        team_type: teamType,
                        team_lead_id: newTeam.team_lead_id,
                        team_lead_name: leadUser?.full_name || '',
                        team_lead_role: leadUser?.role || '',
                        assigned_clients: newTeam.assigned_clients,
                        description: `Team led by ${leadUser?.full_name || 'team lead'}`
                      })
                    });

                    if (response.ok) {
                      toast.success('Team created successfully!');
                      setShowCreateTeamModal(false);
                      setNewTeam({ team_name: '', team_lead_id: '', assigned_clients: [] });
                      onRefresh();
                    } else {
                      const error = await response.json();
                      toast.error(error.detail || 'Failed to create team');
                    }
                  } catch (error) {
                    console.error('Error creating team:', error);
                    toast.error('Failed to create team');
                  }
                }}
                disabled={!newTeam.team_name.trim() || !newTeam.team_lead_id}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="create-team-submit"
              >
                <Plus className="h-4 w-4" />
                Create Team
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};


export default TeamManagementTab;
